/**
 * Centralized Template Service
 * Wrapper for user-webapp to use centralized templates
 */

import CentralizedTemplateClient from "../shared/CentralizedTemplateClient.js";
import { prepareTemplateContent } from "./templateOrchestrator";
import { hasConstraints } from "../constants/templateConstraints";
import { processLogoElements } from "../shared/logoAspectRatioHelper.js";

class CentralizedTemplateService {
  constructor() {
    // Initialize the centralized client
    this.client = new CentralizedTemplateClient({
      baseUrl: process.env.REACT_APP_BRAND_MS || "http://localhost:3003",
      clientSource: "user-webapp",
      cacheTimeout: 10 * 60 * 1000 // 10 minutes for user-webapp
    });

    // Set auth token if available
    const authToken = localStorage.getItem("authToken");
    if (authToken) {
      this.client.setAuthToken(authToken);
    }
  }

  /**
   * Update auth token
   */
  updateAuthToken(token) {
    this.client.setAuthToken(token);
  }

  /**
   * Get all Polotno templates from central service
   */
  async getPolotnoTemplates() {
    try {
      console.log("📡 Fetching templates from central service...");
      const templates = await this.client.getAllTemplates();

      // Process templates for user-webapp use
      return templates.map((template) =>
        this.processTemplateForDisplay(template)
      );
    } catch (error) {
      console.error("Failed to fetch templates from central service:", error);

      // Fallback to local cache if available
      return this.getFallbackTemplates();
    }
  }

  /**
   * Get a specific template by ID
   */
  async getPolotnoTemplateById(templateId) {
    try {
      const template = await this.client.getTemplateById(templateId);
      return this.processTemplateForDisplay(template);
    } catch (error) {
      console.error(`Failed to fetch template ${templateId}:`, error);
      return null;
    }
  }

  /**
   * Process template for display
   */
  processTemplateForDisplay(template) {
    if (!template) return null;

    // Ensure template has required fields
    return {
      ...template,
      preview: template.preview || this.generatePreviewUrl(template),
      template: this.sanitizeTemplate(template.template)
    };
  }

  /**
   * Sanitize template for safe rendering
   */
  sanitizeTemplate(templateData) {
    if (!templateData?.pages) return templateData;

    const sanitized = JSON.parse(JSON.stringify(templateData));

    // Process SVG sources
    sanitized.pages = sanitized.pages.map((page) => ({
      ...page,
      children: (page.children || []).map((child) => {
        if (child?.type === "svg" && this.isInlineSvg(child.src)) {
          return {
            ...child,
            src: this.convertSvgToDataUri(child.src)
          };
        }
        return child;
      })
    }));

    return sanitized;
  }

  /**
   * Check if value is inline SVG
   */
  isInlineSvg(value) {
    return typeof value === "string" && value.trim().startsWith("<svg");
  }

  /**
   * Convert SVG to data URI
   */
  convertSvgToDataUri(svg) {
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg.trim())}`;
  }

  /**
   * Generate preview URL for template
   */
  generatePreviewUrl(template) {
    // Use template editor preview endpoint
    const editorUrl =
      process.env.REACT_APP_TEMPLATE_EDITOR_URL || "http://localhost:5173";
    return `${editorUrl}/api/templates/preview/${template.id}`;
  }

  /**
   * Apply brand colors to SVG overlay/shape elements that use colorsReplace.
   * Replaces hardcoded accent colors with the brand's primary color.
   */
  applyBrandColors(children, brandDetails) {
    const brandColor = brandDetails?.theme?.brandColors?.[0];
    if (!brandColor) return children;

    // Known SVG base colors used in templates
    const svgBaseColors = ["rgb(0, 161, 255)", "rgb(78, 78, 80)"];

    return children.map((child) => {
      if (child.type !== "svg" || !child.colorsReplace) return child;

      const updatedReplace = { ...child.colorsReplace };
      let changed = false;

      for (const baseColor of svgBaseColors) {
        if (!(baseColor in updatedReplace)) continue;

        const currentValue = updatedReplace[baseColor];
        // Skip elements mapped to white/near-white — these are structural, not accent
        if (
          currentValue === "rgba(255,255,255,1)" ||
          currentValue === "white"
        ) {
          continue;
        }

        updatedReplace[baseColor] = brandColor;
        changed = true;
      }

      return changed ? { ...child, colorsReplace: updatedReplace } : child;
    });
  }

  /**
   * Apply brand fonts to text elements.
   * Uses brand's headingFont for headlines and bodyFont for body/subtitle/CTA.
   * Falls back to Inter if brand fonts aren't set.
   */
  applyBrandFonts(children, brandDetails) {
    const headingFont = brandDetails?.theme?.headingFont || "Inter";
    const bodyFont = brandDetails?.theme?.bodyFont || "Inter";

    return children.map((child) => {
      if (child.type !== "text") return child;

      const slot = child.metadata?.slot || "";
      if (slot === "headline") {
        return { ...child, fontFamily: headingFont, fontWeight: "bold" };
      }
      if (slot === "subheadline" || slot === "cta" || slot === "body") {
        return { ...child, fontFamily: bodyFont };
      }
      return child;
    });
  }

  /**
   * Inject dynamic content into template
   */
  async injectDynamicContent(
    templateData,
    projectData,
    isSmartTemplate = false
  ) {
    if (!templateData?.template?.pages?.[0]?.children) return templateData;

    console.log("🎨 Injecting dynamic content for template:", templateData.id);

    // If this is a smart template, skip orchestration - just inject images/logos
    if (isSmartTemplate) {
      console.log(
        "✨ Smart template - skipping orchestration, only injecting images/logos"
      );
      return await this.injectImagesOnly(templateData, projectData);
    }

    // Check if template has constraint-based configuration
    if (hasConstraints(templateData.id)) {
      console.log("✨ Using constraint-based system");
      return await this.injectWithConstraints(templateData, projectData);
    }

    console.log("⚠️ Using legacy injection");
    return this.injectLegacy(templateData, projectData);
  }

  /**
   * Inject content with constraints
   */
  async injectWithConstraints(templateData, projectData) {
    // Get constraints from central service
    const constraints = await this.client.getTemplateConstraints(
      templateData.id
    );

    if (!constraints) {
      console.warn("No constraints found, falling back to legacy");
      return this.injectLegacy(templateData, projectData);
    }

    // Use template orchestrator for smart content
    let orchestratedData;
    try {
      orchestratedData = await prepareTemplateContent(
        templateData.id,
        projectData.selectedImage,
        projectData.generatedData,
        projectData.brandDetails
      );
    } catch (error) {
      console.error("Orchestration failed:", error);
      return this.injectLegacy(templateData, projectData);
    }

    // Apply orchestrated data to template
    const enhancedTemplate = JSON.parse(JSON.stringify(templateData));
    const page = enhancedTemplate.template.pages[0];

    // Process logo elements first (async)
    if (orchestratedData.processedLogoUrl) {
      const logoWidth = projectData.brandDetails?.theme?.logoWidth;
      const logoHeight = projectData.brandDetails?.theme?.logoHeight;

      console.log("🖼️ Processing logo with aspect ratio adjustment...");
      page.children = await processLogoElements(
        page.children,
        orchestratedData.processedLogoUrl,
        {
          maxWidth: 300,
          maxHeight: 300,
          centerHorizontally: true,
          keepRatio: true,
          precalculatedDimensions:
            logoWidth && logoHeight
              ? { width: logoWidth, height: logoHeight }
              : undefined
        }
      );
    }

    // Apply brand colors to SVG overlays/shapes
    page.children = this.applyBrandColors(
      page.children,
      projectData.brandDetails
    );

    // Apply brand fonts
    page.children = this.applyBrandFonts(
      page.children,
      projectData.brandDetails
    );

    // Apply content to elements
    page.children = page.children.map((child) => {
      const slot = this.getElementSlot(child);

      // Apply text with constraints - try exact match first, then flexible match
      if (orchestratedData.fittedText[slot]) {
        return {
          ...child,
          text: orchestratedData.fittedText[slot].text,
          fontSize: orchestratedData.fittedText[slot].fontSize,
          fill: orchestratedData.textColor
        };
      }

      // Try flexible slot matching for fitted text
      const fittedSlot = Object.keys(orchestratedData.fittedText).find((key) =>
        this.matchesSlot(child, key)
      );
      if (fittedSlot) {
        return {
          ...child,
          text: orchestratedData.fittedText[fittedSlot].text,
          fontSize: orchestratedData.fittedText[fittedSlot].fontSize,
          fill: orchestratedData.textColor
        };
      }

      // Apply background
      if (this.matchesSlot(child, "hero-image") && projectData.selectedImage) {
        return {
          ...child,
          src: projectData.selectedImage
        };
      }

      return child;
    });

    return enhancedTemplate;
  }

  /**
   * Inject only images and logos without text orchestration (for smart templates)
   */
  async injectImagesOnly(templateData, projectData) {
    const enhancedTemplate = JSON.parse(JSON.stringify(templateData));
    const page = enhancedTemplate.template.pages[0];

    // Apply background image
    if (projectData.selectedImage) {
      const backgroundElement = page.children.find((child) =>
        this.matchesSlot(child, "hero-image")
      );

      if (backgroundElement) {
        backgroundElement.src = projectData.selectedImage;
      }
    }

    // Process logo with aspect ratio (if provided)
    const logoUrl =
      projectData.brandDetails?.theme?.logo || projectData.brandDetails?.logo;
    if (logoUrl) {
      const logoWidth = projectData.brandDetails?.theme?.logoWidth;
      const logoHeight = projectData.brandDetails?.theme?.logoHeight;

      console.log(
        "🖼️ Processing logo for smart template (no text orchestration)..."
      );
      page.children = await processLogoElements(page.children, logoUrl, {
        maxWidth: 300,
        maxHeight: 300,
        centerHorizontally: true,
        keepRatio: true,
        precalculatedDimensions:
          logoWidth && logoHeight
            ? { width: logoWidth, height: logoHeight }
            : undefined
      });
    }

    // Apply brand colors to SVG overlays/shapes
    page.children = this.applyBrandColors(
      page.children,
      projectData.brandDetails
    );

    // Apply brand fonts
    page.children = this.applyBrandFonts(
      page.children,
      projectData.brandDetails
    );

    // Don't modify text - it's already optimized from smart template
    console.log("✅ Smart template text preserved - no orchestration needed");

    return enhancedTemplate;
  }

  /**
   * Legacy injection method
   */
  async injectLegacy(templateData, projectData) {
    const enhancedTemplate = JSON.parse(JSON.stringify(templateData));
    const page = enhancedTemplate.template.pages[0];

    // Apply background image
    if (projectData.selectedImage) {
      const backgroundElement = page.children.find((child) =>
        this.matchesSlot(child, "hero-image")
      );

      if (backgroundElement) {
        backgroundElement.src = projectData.selectedImage;
      }
    }

    // Process logo with aspect ratio
    const logoUrl =
      projectData.brandDetails?.theme?.logo || projectData.brandDetails?.logo;
    if (logoUrl) {
      const logoWidth = projectData.brandDetails?.theme?.logoWidth;
      const logoHeight = projectData.brandDetails?.theme?.logoHeight;

      console.log(
        "🖼️ Processing logo with aspect ratio adjustment (legacy)..."
      );
      page.children = await processLogoElements(page.children, logoUrl, {
        maxWidth: 300,
        maxHeight: 300,
        centerHorizontally: true,
        keepRatio: true,
        precalculatedDimensions:
          logoWidth && logoHeight
            ? { width: logoWidth, height: logoHeight }
            : undefined
      });
    }

    // Apply brand colors to SVG overlays/shapes
    page.children = this.applyBrandColors(
      page.children,
      projectData.brandDetails
    );

    // Apply brand fonts
    page.children = this.applyBrandFonts(
      page.children,
      projectData.brandDetails
    );

    // Apply text content — respect maxChars metadata when choosing variants
    const headlines = projectData.generatedData?.headlines || [];
    const descriptions = projectData.generatedData?.description || [];

    page.children = page.children.map((child) => {
      if (child.type === "text") {
        const maxChars = child.metadata?.maxChars || Infinity;

        if (this.matchesSlot(child, "headline")) {
          const fit = this.pickFittingText(headlines, maxChars);
          if (fit) child.text = fit;
        } else if (this.matchesSlot(child, "subheadline")) {
          const candidates = [...descriptions, ...headlines.slice(1)];
          const fit = this.pickFittingText(candidates, maxChars);
          if (fit) child.text = fit;
        } else if (this.matchesSlot(child, "body")) {
          const fit = this.pickFittingText(descriptions, maxChars);
          if (fit) child.text = fit;
        } else if (this.matchesSlot(child, "cta")) {
          child.text = projectData.generatedData?.callToAction || "Learn More";
        }
      }

      return child;
    });

    return enhancedTemplate;
  }

  /**
   * Pick the best fitting text from candidates within maxChars.
   * Handles both plain strings and variant objects ({text, characterCount}).
   * Returns the longest variant that fits (best use of space).
   */
  pickFittingText(candidates, maxChars) {
    if (!candidates || candidates.length === 0) return null;

    // Normalize to plain strings
    const texts = candidates
      .map((c) => (typeof c === "object" && c !== null ? c.text : c))
      .filter(Boolean);

    if (texts.length === 0) return null;
    if (maxChars === Infinity) return texts[0];

    // Find all that fit
    const fitting = texts.filter((t) => t.length <= maxChars);
    if (fitting.length > 0) {
      // Return the longest one that fits (best use of space)
      return fitting.sort((a, b) => b.length - a.length)[0];
    }

    // Nothing fits — return the shortest available (least overflow)
    return [...texts].sort((a, b) => a.length - b.length)[0] || null;
  }

  /**
   * Get element slot identifier
   */
  getElementSlot(element) {
    // Prefer semantic name over random id for slot matching
    const slot = element.metadata?.slot || element.slot || element.name || "";
    return typeof slot === "string" ? slot.toLowerCase() : "";
  }

  /**
   * Check if element matches a given slot category using flexible name matching
   */
  matchesSlot(element, slotCategory) {
    const slot = this.getElementSlot(element);
    if (!slot) return false;

    switch (slotCategory) {
      case "headline":
      case "heading":
        return (
          (slot.includes("headline") || slot.includes("heading")) &&
          !slot.includes("sub")
        );
      case "subheadline":
      case "subheading":
        return (
          slot.includes("subheadline") ||
          slot.includes("subheading") ||
          slot.includes("sub head")
        );
      case "body":
      case "description":
        return (
          slot.includes("body") ||
          slot.includes("description") ||
          slot.includes("primary text")
        );
      case "cta":
        return (
          slot.includes("cta") ||
          slot.includes("call to action") ||
          slot.includes("button")
        );
      case "hero-image":
        return slot.includes("background") || slot.includes("hero");
      case "logo":
        return slot.includes("logo");
      default:
        return slot === slotCategory;
    }
  }

  /**
   * Get fallback templates (local backup)
   */
  getFallbackTemplates() {
    console.log("⚠️ Using fallback templates");

    // Return minimal set of templates as fallback
    return [
      {
        id: "fallback-square",
        name: "Basic Square Template",
        category: "basic",
        type: "SQUARE",
        tags: ["fallback", "basic"],
        template: {
          width: 1080,
          height: 1080,
          pages: [
            {
              id: "page_1",
              background: "white",
              children: []
            }
          ]
        }
      }
    ];
  }

  /**
   * Save template to central service
   */
  async saveTemplate(templateData) {
    try {
      return await this.client.createTemplate(templateData);
    } catch (error) {
      console.error("Failed to save template:", error);
      throw error;
    }
  }

  /**
   * Update template in central service
   */
  async updateTemplate(templateId, updateData) {
    try {
      return await this.client.updateTemplate(templateId, updateData);
    } catch (error) {
      console.error("Failed to update template:", error);
      throw error;
    }
  }

  /**
   * Search templates
   */
  async searchTemplates(query, filters) {
    try {
      return await this.client.searchTemplates(query, filters);
    } catch (error) {
      console.error("Template search failed:", error);
      return [];
    }
  }

  /**
   * Get template statistics
   */
  async getTemplateStats() {
    try {
      return await this.client.getTemplateStats();
    } catch (error) {
      console.error("Failed to get template stats:", error);
      return null;
    }
  }
}

// Export singleton instance
export const centralizedTemplateService = new CentralizedTemplateService();
export default centralizedTemplateService;
